// components/ModifierAvoir.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaPlus, FaTrash, FaSave } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as LoadingIcons from "react-loading-icons";
import { useSelector } from "react-redux";
import { getAvoir, updateAvoir } from "../services/AvoirsService";
import { tvainfo } from "../services/LoginService";

const ModifierAvoir = () => {
    const { id } = useParams();
    const token = useSelector((state) => state.auth.token);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [tva_inclus, setTvaInclus] = useState(false);
    const [activeRates, setActiveRates] = useState([]);
    const [avoir, setAvoir] = useState(null);

    const justificatifs = [
        "Rupture de stock",
        "Produit défectueux",
        "Erreur de livraison",
        "Annulation client",
        "Retour marchandise",
        "Autres justificatifs",
    ];

    // 🔹 Charger l'avoir + TVA
    useEffect(() => {
        if (!token) return navigate("/login");

        const fetchData = async () => {
            try {
                const resTVA = await tvainfo(token);
                const resAvoir = await getAvoir(id);

                if (resTVA.success) {
                    setTvaInclus(resTVA.tva_inclus ?? false);
                    const rates = resTVA.tva_rates || {};
                    const active = Object.keys(rates).filter(k => rates[k]).sort((a, b) => parseFloat(a) - parseFloat(b));
                    setActiveRates(active);
                }

                setAvoir(resAvoir);
            } catch (err) {
                console.error(err);
                toast.error("Erreur lors du chargement de l'avoir.");
                navigate("/EspaceClient/avoirs");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, navigate, token]);

    // 🔹 Calcul des totaux
    const totaux = useMemo(() => {
        if (!avoir) return { sous_total_centimes: 0, tva_centimes: 0, total_centimes: 0, net_a_payer: 0 };

        let totalHT = 0;
        let totalTVA = 0;
        avoir.lignes.forEach(ligne => {
            const ht = parseFloat(ligne.total_ligne_centimes) || 0;
            const taux = parseFloat(ligne.taux_tva) || 0;
            totalHT += ht;
            totalTVA += ht * taux / 100;
        });
        const totalTTC = totalHT + totalTVA;
        const totalFactureTTC = parseFloat(avoir.facture?.total_centimes) || 0;
        const netAPayer = totalFactureTTC - totalTTC;

        return { sous_total_centimes: totalHT, tva_centimes: totalTVA, total_centimes: totalTTC, net_a_payer: netAPayer < 0 ? 0 : netAPayer };
    }, [avoir]);

    const tvaParTaux = useMemo(() => {
        if (!avoir) return {};
        const result = {};
        avoir.lignes.forEach(ligne => {
            const ht = parseFloat(ligne.total_ligne_centimes) || 0;
            const taux = parseFloat(ligne.taux_tva) || 0;
            const key = taux.toFixed(2);
            if (!result[key]) result[key] = 0;
            result[key] += ht * taux / 100;
        });
        return result;
    }, [avoir]);

    const updateLigne = (index, field, value) => {
        const newLignes = [...avoir.lignes];
        newLignes[index][field] = value;

        // Recalcul total ligne
        const prix = parseFloat(newLignes[index].prix_unitaire_centimes) || 0;
        const qte = parseFloat(newLignes[index].quantite) || 0;
        const remise = parseFloat(newLignes[index].taux_remise) || 0;
        newLignes[index].total_ligne_centimes = prix * qte * (1 - remise / 100);

        setAvoir({ ...avoir, lignes: newLignes });
    };

    const addLigne = () => {
        setAvoir(prev => ({
            ...prev,
            lignes: [...prev.lignes, {
                designation: "",
                quantite: 1,
                unite: "",
                prix_unitaire_centimes: 0,
                taux_tva: activeRates[0] ? parseFloat(activeRates[0]) : 0,
                taux_remise: 0,
                total_ligne_centimes: 0
            }]
        }));
    };

    const removeLigne = (index) => {
        const newLignes = [...avoir.lignes];
        newLignes.splice(index, 1);
        setAvoir({ ...avoir, lignes: newLignes });
    };

    const handleSave = async () => {
        try {
            if (!avoir.notes_client?.trim()) { toast.error("Veuillez renseigner la note client."); return; }
            if (!avoir.notes_internes?.trim()) { toast.error("Veuillez renseigner la note interne."); return; }

            const totalAvoir = avoir.lignes.reduce((acc, l) => acc + parseFloat(l.total_ligne_centimes), 0);
            if (avoir.facture && totalAvoir > avoir.facture.total_centimes) {
                toast.error("Le montant total de l'avoir ne peut pas dépasser le montant de la facture.");
                return;
            }

            setLoading(true);
            await updateAvoir(id, {
                ...avoir,
                sous_total_centimes: totaux.sous_total_centimes,
                tva_centimes: totaux.tva_centimes,
                total_centimes: totaux.total_centimes
            });
            setLoading(false);
            toast.success("Avoir modifié !");
            navigate("/EspaceClient/avoirs");
        } catch (err) {
            console.error(err);
            setLoading(false);
            toast.error(err.message || "Erreur lors de la modification.");
        }
    };

    if (loading || !avoir) return (
        <div className="flex flex-col justify-center min-h-screen items-center">
            <LoadingIcons.SpinningCircles stroke="#2218eeff" fill="#2218eeff" strokeWidth={3} height={40} width={40} />
            <span className="ml-3 text-gray-600 font-medium">Chargement de l'avoir...</span>
        </div>
    );

    return (
        <div className="min-h-screen p-6 flex flex-col gap-4">
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="flex justify-between items-center mb-4">
                <h1 className="font-bold text-2xl">Modifier Avoir #{avoir.numero}</h1>
                <button onClick={handleSave} className="bg-color3 hover:bg-color3/80 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                    <FaSave /> Enregistrer
                </button>
            </div>

            <p className="mb-2">Client : {avoir.facture?.client?.nom_affichage}</p>

            {/* Notes */}
            <div className="bg-white shadow-md rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="font-medium">Date d'émission</label>
                        <input type="date" value={avoir.date_emission} onChange={e => setAvoir({ ...avoir, date_emission: e.target.value })} className="border rounded-md px-2 py-1 w-full" />
                    </div>
                    <div>
                        <label className="font-medium">Statut</label>
                        <select value={avoir.statut} onChange={e => setAvoir({ ...avoir, statut: e.target.value })} className="border rounded-md px-2 py-1 w-full">
                            <option value="brouillon">Brouillon</option>
                            <option value="envoye">Envoyé</option>
                            <option value="rembourse">Remboursé</option>
                            <option value="impute">Imputé</option>
                            <option value="annule">Annulé</option>
                        </select>
                    </div>
                </div>

                <div className="mt-4">
                    <label className="font-medium">Notes client</label>
                    <select value={avoir.notes_client} onChange={e => setAvoir({ ...avoir, notes_client: e.target.value })} className="border rounded-md w-full p-2">
                        {justificatifs.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
                    </select>
                </div>
                <div className="mt-2">
                    <label className="font-medium">Notes internes</label>
                    <select value={avoir.notes_internes} onChange={e => setAvoir({ ...avoir, notes_internes: e.target.value })} className="border rounded-md w-full p-2">
                        {justificatifs.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
                    </select>
                </div>
            </div>

            {/* Lignes */}
            <div className="bg-white shadow-md rounded-lg p-4">
                <h2 className="font-semibold mb-2">Lignes de l'avoir</h2>
                <table className="w-full text-sm text-left border-collapse mb-4">
                    <thead className="bg-color1 text-color2 uppercase text-xs font-semibold">
                        <tr>
                            <th>Désignation</th>
                            <th className="w-24 text-center">Qté</th>
                            <th className="w-24 text-center">Unité</th>
                            <th className="w-32 text-center">Prix Unitaire</th>
                            <th className="w-24 text-center">TVA (%)</th>
                            <th className="w-24 text-center">Remise (%)</th>
                            <th className="w-32 text-center">Total HT</th>
                            <th className="w-16">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {avoir.lignes.map((ligne, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50">
                                <td><textarea value={ligne.designation} onChange={e => updateLigne(index, "designation", e.target.value)} className="w-full border rounded-md px-1 py-1 resize-none" rows={2} /></td>
                                <td><input type="number" min={1} value={ligne.quantite} onChange={e => updateLigne(index, "quantite", parseFloat(e.target.value) || 0)} className="w-full border rounded-md px-1 py-1 text-center" /></td>
                                <td><input type="text" value={ligne.unite} onChange={e => updateLigne(index, "unite", e.target.value)} className="w-full border rounded-md px-1 py-1 text-center" /></td>
                                <td><input type="number" min={0} value={ligne.prix_unitaire_centimes} onChange={e => updateLigne(index, "prix_unitaire_centimes", parseFloat(e.target.value) || 0)} className="w-full border rounded-md px-1 py-1 text-center" /></td>
                                <td>
                                    {tva_inclus && activeRates.length > 0 ? (
                                        <select value={ligne.taux_tva} onChange={e => updateLigne(index, "taux_tva", parseFloat(e.target.value))} className="w-full border rounded-md px-1 py-1 text-center">
                                            {activeRates.map(rate => <option key={rate} value={parseFloat(rate)}>{rate}</option>)}
                                        </select>
                                    ) : <p className="text-center">-</p>}
                                </td>
                                <td><input type="number" min={0} max={100} value={ligne.taux_remise} onChange={e => updateLigne(index, "taux_remise", parseFloat(e.target.value) || 0)} className="w-full border rounded-md px-1 py-1 text-center" /></td>
                                <td className="text-center font-bold">{ligne.total_ligne_centimes.toFixed(2)} €</td>
                                <td className="text-center">
                                    <button className="text-red-600 hover:text-red-800" onClick={() => removeLigne(index)}><FaTrash /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="flex justify-end mb-4">
                    <button onClick={addLigne} className="bg-color3 hover:bg-color3/80 text-white px-3 py-1 rounded-lg flex items-center gap-1"><FaPlus /> Ajouter ligne</button>
                </div>

                {/* Totaux */}
                <div className="grid grid-cols-[180px_120px] gap-y-1 justify-end items-end mt-2">
                    <span className="font-bold text-left">Total HT :</span>
                    <span className="text-right">{totaux.sous_total_centimes.toFixed(2)} €</span>

                    {Object.keys(tvaParTaux).map(taux => (
                        <React.Fragment key={taux}>
                            <span className="font-bold text-left">TVA {taux}% :</span>
                            <span className="text-right">{tvaParTaux[taux].toFixed(2)} €</span>
                        </React.Fragment>
                    ))}

                    <span className="font-bold text-left">Total TTC Avoir :</span>
                    <span className="text-right">{totaux.total_centimes.toFixed(2)} €</span>

                    <span className="font-bold text-left">Total TTC Facture :</span>
                    <span className="text-right">{avoir.facture?.total_centimes.toFixed(2)} €</span>

                    <span className="font-bold text-left">Net à payer :</span>
                    <span className="text-right">{totaux.net_a_payer.toFixed(2)} €</span>
                </div>
            </div>
        </div>
    );
};

export default ModifierAvoir;
